import { useState } from "react";
import { Check, ChevronDown, Plus } from "lucide-react";
import { iosSnapshotPageSize, useOrganizationIOSSnapshots, type IOSSnapshot } from "@/client/backend/iosSnapshots";
import { Button } from "@/components/ui/button";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export function PlaygroundSnapshotPicker({ organizationId, value, creating, onSelect, onCreate, disabled, canRead }: {
  organizationId?: string;
  value?: string;
  creating: boolean;
  onSelect: (key?: string, snapshot?: IOSSnapshot) => void;
  onCreate: (key: string) => void;
  disabled: boolean;
  canRead: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const prefix = search.trim();
  const { data, error, mutate } = useOrganizationIOSSnapshots(open && canRead ? organizationId : undefined, prefix, offset);

  function select(key?: string, snapshot?: IOSSnapshot) {
    onSelect(key, snapshot);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={next => {
      setOpen(next);
      if (next) { setSearch(""); setOffset(0); }
    }}>
      <PopoverTrigger asChild>
        <Button
          id="snapshot-select"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Snapshot"
          aria-describedby="snapshot-description snapshot-status"
          disabled={disabled}
          className="h-auto min-h-9 w-full justify-between gap-2 px-3 py-1.5 text-left font-normal"
        >
          <span className="min-w-0 whitespace-normal break-all">{creating ? "Create snapshot" : value || "None"}</span>
          <ChevronDown aria-hidden className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)] p-0">
        <Command label="Search snapshots" shouldFilter={false}>
          <CommandInput
            aria-label="Search snapshots"
            placeholder="Search snapshots..."
            value={search}
            onValueChange={value => { setSearch(value); setOffset(0); }}
          />
          <CommandList>
            <CommandGroup>
              <CommandItem value="none" onSelect={() => select()}>
                <Check aria-hidden className={!value && !creating ? "opacity-100" : "opacity-0"} />
                None
              </CommandItem>
              {canRead && (
                <CommandItem value="create" onSelect={() => { onCreate(prefix); setOpen(false); }}>
                  <Plus aria-hidden />
                  Create snapshot
                </CommandItem>
              )}
            </CommandGroup>
            {canRead && !error && data === undefined && <p role="status" className="px-3 py-3 text-xs text-muted-foreground">Loading snapshots...</p>}
            {error && (
              <div className="space-y-2 px-3 py-3">
                <p role="alert" className="text-xs text-destructive">Could not load snapshots.</p>
                <Button variant="outline" size="sm" onClick={() => void mutate()}>Retry snapshots</Button>
              </div>
            )}
            {!error && data?.snapshots.length === 0 && <p role="status" className="px-3 py-3 text-xs text-muted-foreground">
              {prefix ? "No matching snapshots." : "No snapshots yet. Create one to save your next device."}
            </p>}
            {!error && !!data?.snapshots.length && (
              <CommandGroup heading="Snapshots">
                {data.snapshots.map(snapshot => (
                  <CommandItem
                    key={snapshot.key}
                    value={`snapshot:${snapshot.key}`}
                    aria-label={`${snapshot.key}, ${!snapshot.saved ? "Not saved yet" : snapshot.iosVersion ? `iOS ${snapshot.iosVersion}` : "Saved"}`}
                    onSelect={() => select(snapshot.key, snapshot)}
                  >
                    <Check aria-hidden className={!creating && value === snapshot.key ? "opacity-100" : "opacity-0"} />
                    <span className="min-w-0 flex-1 break-all">{snapshot.key}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {!snapshot.saved ? "Not saved yet" : snapshot.iosVersion ? `iOS ${snapshot.iosVersion}` : "Saved"}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
          {(offset > 0 || data?.nextOffset !== undefined) && (
            <div className="flex justify-between border-t p-2">
              <Button variant="ghost" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - iosSnapshotPageSize))}>Previous</Button>
              <Button variant="ghost" size="sm" disabled={data?.nextOffset === undefined} onClick={() => setOffset(data?.nextOffset ?? offset)}>Next</Button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
